// src/components/ui/Modal.tsx
import { useEffect, useId, useRef, type ReactNode } from 'react';
import { brand } from '@/lib/tokens';

type Variant = 'danger' | 'primary';
type Tone = 'success' | 'error' | 'info';

const ICON_FOR_TONE: Record<Tone, string> = {
  success: 'check_circle',
  error: 'error',
  info: 'info',
};

const COLOR_FOR_TONE: Record<Tone, string> = {
  success: brand.success,
  error: brand.error,
  info: brand.accent,
};

type Props = {
  open: boolean;
  title: string;
  body: ReactNode;
  confirmLabel: string;
  /** Omit cancelLabel (or pass null) for a single-button acknowledge dialog. */
  cancelLabel?: string | null;
  confirmVariant?: Variant;
  /** Renders a status icon above the title. */
  tone?: Tone;
  onConfirm: () => void;
  onCancel: () => void;
};

const FOCUSABLE_SELECTOR =
  'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])';

export function Modal({
  open,
  title,
  body,
  confirmLabel,
  cancelLabel = 'Cancel',
  confirmVariant = 'primary',
  tone,
  onConfirm,
  onCancel,
}: Props) {
  const titleId = useId();
  const cardRef = useRef<HTMLDivElement>(null);
  const previouslyFocused = useRef<HTMLElement | null>(null);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onCancel();
        return;
      }
      if (e.key !== 'Tab' || !cardRef.current) return;
      const focusable = Array.from(cardRef.current.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR));
      if (focusable.length === 0) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onCancel]);

  useEffect(() => {
    if (!open) return;
    previouslyFocused.current = document.activeElement as HTMLElement | null;
    const focusable = cardRef.current?.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR);
    (focusable?.[0] ?? cardRef.current)?.focus();
    return () => {
      previouslyFocused.current?.focus?.();
    };
  }, [open]);

  if (!open) return null;

  return (
    <div
      className="glass-overlay fixed inset-0 z-50 flex items-center justify-center px-6 py-6"
      onClick={onCancel}
      role="dialog"
      aria-modal="true"
      aria-labelledby={titleId}
    >
      <div
        ref={cardRef}
        className="glass-card glass-top-highlight slide-up-modal"
        style={{ width: '100%', maxWidth: 460, padding: 30 }}
        onClick={(e) => e.stopPropagation()}
        tabIndex={-1}
      >
        {tone && (
          <span
            className="material-symbols-outlined"
            aria-hidden
            style={{ fontSize: 32, color: COLOR_FOR_TONE[tone], display: 'block', marginBottom: 10 }}
          >
            {ICON_FOR_TONE[tone]}
          </span>
        )}
        <h2
          id={titleId}
          className="font-serif"
          style={{ fontSize: 22, lineHeight: 1.2, color: 'var(--text-primary)' }}
        >
          {title}
        </h2>
        <div
          className="mt-3 text-sm leading-relaxed"
          style={{ color: 'rgba(var(--fg-rgb),0.6)' }}
        >
          {body}
        </div>
        <div className="mt-7 flex items-center justify-end gap-2">
          {cancelLabel && (
            <button className="noir-button" onClick={onCancel}>
              {cancelLabel}
            </button>
          )}
          {confirmVariant === 'danger' ? (
            <button className="noir-button noir-button-danger" onClick={onConfirm}>
              {confirmLabel}
            </button>
          ) : (
            <button className="noir-button noir-button-primary" onClick={onConfirm}>
              {confirmLabel}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
